/**
 * React Hook for Internet Connectivity
 *
 * Provides easy access to internet connectivity status across React components.
 * Uses the centralized InternetConnectivityService for consistent state management.
 */

import { useState, useEffect, useCallback } from 'react';
import internetConnectivityService from '../services/internetConnectivityService';

// Consistent log prefix for easy filtering
const LOG_PREFIX = '[INTERNET_CONNECTIVITY]';

/**
 * Hook to track internet connectivity status
 * @param {Object} options - Configuration options
 * @param {boolean} options.startMonitoring - Whether to start periodic monitoring (default: false)
 * @param {number} options.monitoringInterval - Monitoring interval in ms (default: 30000)
 * @param {boolean} options.checkOnMount - Whether to force check on component mount (default: true)
 * @returns {Object} Connectivity state and methods
 */
export const useInternetConnectivity = (options = {}) => {
    const {
        startMonitoring = false,
        monitoringInterval = 30000,
        checkOnMount = true
    } = options;

    const [connectivity, setConnectivity] = useState(() => {
        return internetConnectivityService.getStatus();
    });

    const [isChecking, setIsChecking] = useState(false);

    const checkConnectivity = useCallback(async () => {
        setIsChecking(true);
        try {
            await internetConnectivityService.forceCheck();
        } finally {
            setIsChecking(false);
        }
    }, []);

    useEffect(() => {
        const unsubscribe = internetConnectivityService.subscribe((status) => {
            setConnectivity(status);
        });

        if (checkOnMount) {
            checkConnectivity();
        }

        if (startMonitoring) {
            internetConnectivityService.startMonitoring(monitoringInterval);
        }

        return () => {
            unsubscribe();
            if (startMonitoring) {
                internetConnectivityService.stopMonitoring();
            }
        };
    }, [checkOnMount, startMonitoring, monitoringInterval, checkConnectivity]);

    return {
        // Status flags
        isOnline: connectivity.isOnline,
        hasInternet: connectivity.hasInternet,
        isConnected: connectivity.isConnected,
        isChecking,

        // Convenience flags for common use cases
        shouldBypassInternetFeatures: !connectivity.hasInternet,
        isCompletelyOffline: !connectivity.isOnline || !connectivity.hasInternet,

        // Methods
        checkConnectivity,

        // Full status object
        status: connectivity
    };
};

/**
 * Hook specifically for determining when to bypass internet-dependent features
 * This is the main hook you'll want to use for conditioning internet-dependent code
 * @returns {Object} { shouldBypass: boolean, hasInternet: boolean, checkConnectivity: function }
 */
export const useInternetBypass = () => {
    const { hasInternet, checkConnectivity } = useInternetConnectivity();

    return {
        shouldBypass: !hasInternet,
        hasInternet,
        checkConnectivity
    };
};

/**
 * Hook for components that need to react to connectivity changes
 * Automatically starts monitoring when mounted
 * @param {Function} onConnectivityChange - Callback when connectivity changes
 * @param {number} monitoringInterval - How often to check (default: 30000ms)
 * @returns {Object} Connectivity state
 */
export const useInternetMonitoring = (onConnectivityChange, monitoringInterval = 30000) => {
    const connectivity = useInternetConnectivity({
        startMonitoring: true,
        monitoringInterval
    });

    // Call the callback when connectivity changes
    useEffect(() => {
        if (onConnectivityChange) {
            onConnectivityChange(connectivity.status);
        }
    }, [connectivity.status, onConnectivityChange]);

    return connectivity;
};

/**
 * Hook for simple online/offline detection
 * Just returns boolean values for basic usage
 * @returns {Object} { isOnline: boolean, hasInternet: boolean }
 */
export const useOnlineStatus = () => {
    const { isOnline, hasInternet } = useInternetConnectivity();

    return {
        isOnline,
        hasInternet
    };
};

export default useInternetConnectivity;